import { supabase } from '../supabase';

const NUTRITION_URL = 'https://nutrition.cipher-health.com';

// The joined Nutrition (plan 005): /nutrition on this same address, with the same sign-in. On for
// everyone since 16 October 2026 (the owner tried it on a phone and a computer first). A device can
// still go back to the separate Nutrition app by opening the app once with ?nutrition=separate (and
// return with ?nutrition=joined).
const SEPARATE_KEY = 'cipher_nutrition_separate';
const OLD_JOINED_KEY = 'cipher_nutrition_joined'; // the opt-in used while it was tried out
export const nutritionJoinedOnThisDevice = () => {
  try { return localStorage.getItem(SEPARATE_KEY) !== '1'; } catch { return true; }
};
export const readNutritionChoice = (loc = window.location, storage = window.localStorage, history = window.history) => {
  try {
    storage.removeItem(OLD_JOINED_KEY);
    const params = new URLSearchParams(loc.search);
    const choice = params.get('nutrition');
    if (choice !== 'joined' && choice !== 'separate') return;
    if (choice === 'separate') storage.setItem(SEPARATE_KEY, '1');
    else storage.removeItem(SEPARATE_KEY);
    params.delete('nutrition');
    const rest = params.toString();
    history.replaceState(history.state, '', loc.pathname + (rest ? `?${rest}` : '') + loc.hash);
  } catch {
    // storage blocked: the joined Nutrition, the default
  }
};
readNutritionChoice();

// Joining from Nutrition (owner, 24 October 2026: one payment system). Nutrition's "join" opens this app
// with ?join=bundle; it is kept for the visit (sessionStorage, so a sign-in in between does not lose it)
// and sends a Nutrition-only account to the bundle payment screen instead of back to Nutrition (Codex's
// review 041, D3). Cleared once they are a member.
const JOIN_KEY = 'cipher_join';
export const readJoinIntent = (loc = window.location, storage = window.sessionStorage, history = window.history) => {
  try {
    const params = new URLSearchParams(loc.search);
    if (params.get('join') !== 'bundle') return;
    storage.setItem(JOIN_KEY, 'bundle');
    params.delete('join');
    const rest = params.toString();
    history.replaceState(history.state, '', loc.pathname + (rest ? `?${rest}` : '') + loc.hash);
  } catch {
    // storage blocked: they land in Nutrition, whose Join button brings them back with the request
  }
};
readJoinIntent();
export const wantsToJoin = (storage = window.sessionStorage) => {
  try { return storage.getItem(JOIN_KEY) === 'bundle'; } catch { return false; }
};
export const clearJoinIntent = (storage = window.sessionStorage) => {
  try { storage.removeItem(JOIN_KEY); } catch { /* nothing kept */ }
};

// To the joined Nutrition, in this tab
export const goToJoinedNutrition = () => window.location.assign('/nutrition');

// Track the Nutrition window so subsequent switches just focus it (no page reload).
let nutritionWin = null;

// Tokens fetched right before opening the window, for the module-level listener
// below to hand over via postMessage once Nutrition asks for them. Cleared after
// one use so a stale value never answers an unrelated later request.
let pendingNutritionTokens = null;

// Listen for session requests from the Nutrition tab. Registered at module level
// (once, regardless of how many components import this file) so it's live
// whenever a Nutrition tab we opened might ask for a session.
window.addEventListener('message', async (event) => {
  if (event.origin !== NUTRITION_URL) return;
  if (event.data?.type !== 'REQUEST_CIPHER_SESSION') return;
  try {
    if (pendingNutritionTokens) {
      event.source.postMessage(
        { type: 'CIPHER_SESSION', ...pendingNutritionTokens },
        NUTRITION_URL
      );
      pendingNutritionTokens = null;
      return;
    }
    // Fallback: no fresh exchange happened (e.g. this request came from a tab
    // opened before this session started) — relay our own session directly.
    // Safe since both apps share one Supabase project, so a valid Fitness
    // token is already a valid Nutrition token too.
    const { data } = await supabase.auth.getSession();
    const s = data?.session;
    if (s?.access_token && s?.refresh_token) {
      event.source.postMessage(
        { type: 'CIPHER_SESSION', access_token: s.access_token, refresh_token: s.refresh_token },
        NUTRITION_URL
      );
    }
  } catch (e) {
    console.error('[crossAppSwitch] getSession failed', e);
  }
});

/**
 * Open (or focus) the Nutrition app, handing over the session without ever
 * putting real access/refresh tokens in a URL.
 *
 * Prefers postMessage (works whenever the new tab is a true popup with a
 * window.opener back-reference) using tokens pre-exchanged via
 * /api/switch-to-nutrition — which also runs the complimentary-access-grant
 * and new-user-creation logic that button used to skip entirely. Falls back to
 * a single-use, short-lived magic-link hash in the URL for the case where
 * postMessage isn't viable (Nutrition's own boot logic decides which path to
 * use — it already prefers postMessage over a URL hash when both are present).
 */
export const openNutritionApp = async () => {
  if (nutritionJoinedOnThisDevice()) {
    // Grant access as the hand-off always did (complimentary clients get the bundle), then go there
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetch('/api/switch-to-nutrition?joined=1', {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      }
    } catch (e) {
      console.error('[crossAppSwitch] joined: access check failed, opening anyway:', e);
    }
    goToJoinedNutrition();
    return;
  }
  if (nutritionWin && !nutritionWin.closed) {
    nutritionWin.focus();
    return;
  }

  // Open synchronously inside the click handler so browsers don't block it as
  // a popup — we navigate it once the handoff is ready.
  const win = window.open('', 'cipher-nutrition');
  nutritionWin = win;

  let url = NUTRITION_URL;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const res = await fetch('/api/switch-to-nutrition', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.access_token && data.refresh_token) {
          pendingNutritionTokens = { access_token: data.access_token, refresh_token: data.refresh_token };
        }
        if (data.hashed_token) {
          url = `${NUTRITION_URL}?type=magiclink&token_hash=${encodeURIComponent(data.hashed_token)}`;
        }
      } else {
        console.error('[crossAppSwitch] switch-to-nutrition failed:', res.status);
      }
    }
  } catch (e) {
    console.error('[crossAppSwitch] handoff fetch failed, opening plain:', e);
  }

  if (win) win.location.href = url;
  else nutritionWin = window.open(url, 'cipher-nutrition');
};
